import { getLocale, t } from './i18n.mjs?v=20260806.100';

const TASK_ROUTE = /^#\/(action|driver-task|pod-edit|create|inspect|queue-action)\/([^?]+)/;

// How long the green check stays before the panel closes. Long enough to be seen
// and no longer: this is the pause between submitting and being able to do the
// next thing, and someone filing ten containers pays it ten times. It was 720ms.
export const SAVED_HOLD_MS = 180;

export function taskWorkspaceCopy(locale = getLocale()) {
  return Object.freeze({
    restoreHidden: t('taskWorkspace.restoreHidden', {}, locale),
    restoreOne: t('taskWorkspace.restoreOne', {}, locale),
    restoreMany: (count) => t('taskWorkspace.restoreMany', { count }, locale),
    lock: t('taskWorkspace.lock', {}, locale),
    unlock: t('taskWorkspace.unlock', {}, locale),
    discard: t('taskWorkspace.discard', {}, locale),
    hide: t('taskWorkspace.hide', {}, locale),
    done: t('taskWorkspace.done', {}, locale),
    task: t('taskWorkspace.task', {}, locale),
    window: t('taskWorkspace.window', {}, locale),
    loading: t('taskWorkspace.loading', {}, locale),
    unavailable: t('taskWorkspace.unavailable', {}, locale),
    openFailed: t('taskWorkspace.openFailed', {}, locale),
  });
}

export function isWorkspaceTaskRoute(route) {
  return TASK_ROUTE.test(String(route || ''));
}

function routeId(route) {
  const match = String(route || '').match(TASK_ROUTE);
  return match ? `${match[1]}:${decodeURIComponent(match[2])}` : null;
}

function compactTitle(panel, route, fallback = t('taskWorkspace.task')) {
  const title = panel.querySelector('h1, h2')?.textContent?.trim();
  return title || decodeURIComponent(String(route).split('/').at(-1)?.split('?')[0] || fallback);
}

function elementLike(value) {
  const ElementClass = globalThis.Element;
  return ElementClass ? value instanceof ElementClass : Boolean(value?.append && value?.querySelector);
}

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value));
}

export function workspaceTileGeometry({
  index = 0,
  route = '',
  viewportWidth = 1440,
  viewportHeight = 900,
  saved = {},
} = {}) {
  const width = Math.max(320, Number(viewportWidth) || 1440);
  const height = Math.max(480, Number(viewportHeight) || 900);
  const inspection = String(route).startsWith('#/inspect/');
  const targetCount = inspection ? 5 : 4;
  const outerGap = width < 900 ? 8 : 16;
  const tileGap = width < 900 ? 10 : 16;
  const topInset = width < 900 ? 62 : 70;
  const bottomInset = width < 900 ? 78 : 16;
  const availableWidth = Math.max(304, width - outerGap * 2);
  const minimumWidth = inspection ? 380 : 520;
  const preferredWidth = inspection ? 480 : 600;
  const maximumColumns = inspection ? 5 : 4;
  const columns = width < 900 ? 1 : Math.max(1, Math.min(
    maximumColumns,
    Math.floor((availableWidth + tileGap) / (minimumWidth + tileGap)),
  ));
  const rows = Math.max(1, Math.ceil(targetCount / columns));
  // Bulk Add is a sheet, not a form: it opens as wide and as tall as the screen allows (Alan, 2026-10-12:
  // "新预报录得时候窗口能否大一些").
  const sheet = String(route).startsWith('#/create/bulk');
  const panelWidth = sheet ? Math.floor(availableWidth) : Math.floor(Math.min(
    preferredWidth,
    (availableWidth - tileGap * (columns - 1)) / columns,
  ));
  const usableHeight = Math.max(260, height - topInset - bottomInset);
  const panelMaxHeight = sheet ? Math.floor(usableHeight) : Math.floor(Math.min(
    680,
    inspection ? (usableHeight - tileGap * (rows - 1)) / rows : usableHeight,
  ));
  const column = Math.max(0, Number(index) || 0) % columns;
  const row = Math.floor(Math.max(0, Number(index) || 0) / columns);
  const horizontalStep = columns > 1
    ? (availableWidth - panelWidth) / (columns - 1)
    : 0;
  const verticalStep = rows > 1
    ? (usableHeight - panelMaxHeight) / (rows - 1)
    : 0;
  const automaticLeft = Math.round(outerGap + column * horizontalStep);
  const automaticTop = Math.round(topInset + row * verticalStep);
  const savedLeft = Number(saved.left);
  const savedTop = Number(saved.top);
  return Object.freeze({
    left: Number.isFinite(savedLeft) ? clamp(savedLeft, 8, Math.max(8, width - panelWidth - 8)) : automaticLeft,
    top: Number.isFinite(savedTop) ? clamp(savedTop, 58, Math.max(58, height - 180)) : automaticTop,
    panelWidth,
    panelMaxHeight,
    columns,
    rows,
  });
}

const CLOSE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10M17 7 7 17"/></svg>';
const DONE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 12.5 3.2 3.2L17.5 8"/></svg>';

export function mountTaskWorkspace({
  root,
  mountRoot = globalThis.document?.body,
  dockHost = null,
  document = globalThis.document,
  renderRoute,
  onDone = async () => false,
  storage = globalThis.sessionStorage,
  storageKey = 'ay-tms-open-task-workspace',
} = {}) {
  if (!elementLike(root) || !elementLike(mountRoot)
    || !document || typeof renderRoute !== 'function' || typeof onDone !== 'function') {
    return Object.freeze({
      closeAll() {}, closeRoute() {}, async confirmRoute() { return false; }, destroy() {}, hideUnlocked() {}, open() {},
      restoreHidden() {}, setDockHost() {},
    });
  }

  const workspace = document.createElement('section');
  const copy = taskWorkspaceCopy();
  workspace.className = 'task-workspace';
  workspace.innerHTML = '<div class="task-workspace-panels" aria-live="polite"></div>';
  mountRoot.append(workspace);
  const panels = workspace.querySelector('.task-workspace-panels');

  const restore = document.createElement('button');
  restore.className = 'task-workspace-restore';
  restore.type = 'button';
  restore.hidden = true;
  restore.setAttribute('aria-label', copy.restoreHidden);
  restore.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 1 0 3-6.2"/><path d="M4 4v6h6"/></svg><span data-task-workspace-restore-count></span>';

  const tasks = new Map();
  let currentDockHost = null;
  let destroyed = false;
  let topZ = 2600;

  function safeReadState() {
    try {
      const parsed = JSON.parse(storage?.getItem(storageKey) || '{}');
      return parsed && Array.isArray(parsed.tasks) ? parsed : { tasks: [] };
    } catch {
      return { tasks: [] };
    }
  }

  function fieldKey(field, index) {
    return field.name || field.id || `field-${index}`;
  }

  function serializePanel(panel) {
    return [...panel.querySelectorAll('input, select, textarea')].flatMap((field, index) => {
      if (field.type === 'file' || field.type === 'password') return [];
      return [{
        key: fieldKey(field, index), type: field.type, value: field.value,
        checked: field.checked === true,
      }];
    });
  }

  function restorePanel(panel, fields = []) {
    const controls = [...panel.querySelectorAll('input, select, textarea')];
    for (const saved of fields) {
      const field = controls.find((control, index) => fieldKey(control, index) === saved.key);
      if (!field || field.type === 'file' || field.type === 'password') continue;
      if (field.type === 'checkbox' || field.type === 'radio') field.checked = saved.checked === true;
      else field.value = saved.value ?? '';
    }
  }

  function persist() {
    if (!storage || destroyed) return;
    try {
      storage.setItem(storageKey, JSON.stringify({
        tasks: [...tasks.values()].filter((task) => !task.ephemeral).map((task) => ({
          route: task.route,
          fields: serializePanel(task.panel),
          left: task.left,
          top: task.top,
          locked: task.locked,
          visible: task.visible,
        })),
      }));
    } catch {
      // Mounted DOM remains authoritative when session persistence is unavailable.
    }
  }

  function syncRestore() {
    // Nothing is ever hidden now - closing discards - so there is nothing to
    // restore and no button for it (Alan, 2026-08-09). Any tab persisted as hidden
    // by an older build is dropped rather than offered back, so the control cannot
    // reappear from storage.
    for (const task of [...tasks.values()]) if (!task.visible) close(task.id);
    const hiddenCount = 0;
    currentDockHost?.classList.remove('has-hidden-task-windows');
    restore.hidden = true;
    restore.querySelector('[data-task-workspace-restore-count]').textContent = hiddenCount
      ? String(hiddenCount) : '';
    restore.setAttribute('aria-label', hiddenCount === 1 ? copy.restoreOne : copy.restoreMany(hiddenCount));
  }

  function setDockHost(nextHost) {
    currentDockHost?.classList.remove('has-hidden-task-windows');
    currentDockHost = elementLike(nextHost) ? nextHost : null;
    (currentDockHost || mountRoot).append(restore);
    syncRestore();
  }

  setDockHost(dockHost);

  function motionEnabled() {
    return document.documentElement?.dataset?.motion !== 'off';
  }

  // 220ms was a fifth of a second between pressing the plus and being able to type
  // the container number, on a form opened dozens of times a day (Alan, 2026-08-10:
  // "decrease motion when add do so can make add task process faster"). 90ms is
  // still enough to see where the panel came from without waiting for it.
  function animate(element, keyframes) {
    if (!motionEnabled()) return;
    element.animate?.(keyframes, { duration: 90, easing: 'cubic-bezier(.22, 1, .36, 1)' });
  }

  function syncTask(task) {
    task.panel.hidden = !task.visible;
    task.panel.dataset.locked = String(task.locked);
    task.panel.style.left = `${task.left}px`;
    task.panel.style.top = `${task.top}px`;
    // No lock control any more, so nothing to sync. task.locked survives as the
    // flag closeAll() honours; it is simply never set from the chrome.
    const lock = task.panel.querySelector('.task-workspace-lock');
    if (lock) {
      lock.setAttribute('aria-pressed', String(task.locked));
      lock.setAttribute('aria-label', task.locked ? copy.unlock : copy.lock);
    }
  }

  function bringToFront(task) {
    if (!task) return;
    topZ += 1;
    task.panel.style.zIndex = String(topZ);
  }

  function show(task, { focus = true } = {}) {
    if (!task) return;
    task.visible = true;
    syncTask(task);
    syncRestore();
    bringToFront(task);
    animate(task.panel, [
      { opacity: 0, transform: 'translateY(8px) scale(.98)' },
      { opacity: 1, transform: 'translateY(0) scale(1)' },
    ]);
    if (focus) task.panel.querySelector('input, select, textarea, button, a[href]')?.focus();
    persist();
  }

  /**
   * Closing a tab throws it away (Alan, 2026-08-09: "点出 tab 直接清楚里面的信息，不再
   * 带有 roll back 按键。想要保存只能点击锁按钮固定 tab").
   *
   * It used to be hidden instead, and a Restore control brought it back with
   * everything still typed in it. That made closing ambiguous - two ways to get rid
   * of a tab, one of which secretly kept it - and left half-finished forms parked
   * off screen. The lock is the one way to keep a tab now, and it already refuses
   * to be closed.
   */
  function hide(task, { force = false } = {}) {
    if (!task?.visible || (task.locked && !force)) return false;
    close(task.id);
    return true;
  }

  function hideUnlocked() {
    let changed = false;
    for (const task of tasks.values()) changed = hide(task) || changed;
    return changed;
  }

  function restoreHidden() {
    const hidden = [...tasks.values()].filter((task) => !task.visible);
    hidden.forEach((task, index) => show(task, { focus: index === hidden.length - 1 }));
    syncRestore();
  }


  function close(id) {
    const task = tasks.get(id);
    if (!task) return;
    task.cleanup?.();
    task.dragCleanup?.();
    task.panel.remove();
    tasks.delete(id);
    syncRestore();
    persist();
  }

  /**
   * The acknowledgement after a successful save: a green check, then the panel
   * goes.
   *
   * It used to take nearly a second. It flashed every filled control in the form
   * one by one, drew the check over the top, and then held the panel open for
   * 720ms so the animation could finish - so the reward for submitting was
   * waiting (Alan, 2026-08-08). Filing ten containers meant sitting through it
   * ten times.
   *
   * What is left is the smallest thing that still registers: the check appears
   * with no animation at all, and the panel closes in SAVED_HOLD_MS. The
   * acknowledgement that survives the close is the toast the caller raises, which
   * stays for three and a half seconds and does not hold anything up. Two signals,
   * no wait.
   */
  async function confirmRoute(route, { label = 'Updated' } = {}) {
    const id = routeId(route);
    const task = id ? tasks.get(id) : null;
    if (!task || destroyed) return false;
    bringToFront(task);
    task.panel.dataset.saveState = 'confirmed';
    task.panel.setAttribute('aria-busy', 'true');
    const confirmation = document.createElement('div');
    confirmation.className = 'task-workspace-save-confirmation';
    confirmation.setAttribute('role', 'status');
    confirmation.setAttribute('aria-live', 'assertive');
    const mark = document.createElement('span');
    mark.className = 'task-workspace-save-confirmation__mark';
    mark.innerHTML = DONE_ICON;
    const text = document.createElement('strong');
    text.textContent = String(label || 'Updated');
    confirmation.append(mark, text);
    task.panel.append(confirmation);
    await new Promise((resolve) => globalThis.setTimeout(resolve, SAVED_HOLD_MS));
    if (tasks.has(id)) close(id);
    return true;
  }

  function defaultPosition(index, saved = {}, route = '') {
    const width = globalThis.innerWidth || document.documentElement?.clientWidth || 1440;
    const height = globalThis.innerHeight || document.documentElement?.clientHeight || 900;
    return workspaceTileGeometry({
      index, route, viewportWidth: width, viewportHeight: height, saved,
    });
  }

  function mountDragging(task) {
    const handle = task.panel.querySelector('.task-workspace-panel__chrome');
    let drag = null;
    const onPointerDown = (event) => {
      bringToFront(task);
      if (event.button !== 0 || event.target.closest('button')
        || globalThis.matchMedia?.('(max-width: 899px)')?.matches) return;
      const rect = task.panel.getBoundingClientRect();
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rect.left, top: rect.top };
      handle.setPointerCapture?.(event.pointerId);
      task.panel.dataset.dragging = 'true';
      event.preventDefault();
    };
    const onPointerMove = (event) => {
      if (!drag || drag.id !== event.pointerId) return;
      const width = globalThis.innerWidth || document.documentElement?.clientWidth || 1440;
      const height = globalThis.innerHeight || document.documentElement?.clientHeight || 900;
      const panelWidth = task.panel.offsetWidth || 560;
      const panelHeight = task.panel.offsetHeight || 360;
      task.left = clamp(drag.left + event.clientX - drag.x, 8, Math.max(8, width - panelWidth - 8));
      task.top = clamp(drag.top + event.clientY - drag.y, 58, Math.max(58, height - panelHeight - 8));
      syncTask(task);
    };
    const onPointerUp = (event) => {
      if (!drag || drag.id !== event.pointerId) return;
      handle.releasePointerCapture?.(event.pointerId);
      drag = null;
      task.panel.dataset.dragging = 'false';
      persist();
    };
    handle.addEventListener('pointerdown', onPointerDown);
    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', onPointerUp);
    handle.addEventListener('pointercancel', onPointerUp);
    return () => {
      handle.removeEventListener('pointerdown', onPointerDown);
      handle.removeEventListener('pointermove', onPointerMove);
      handle.removeEventListener('pointerup', onPointerUp);
      handle.removeEventListener('pointercancel', onPointerUp);
    };
  }

  async function renderTask(task, { savedFields = null } = {}) {
    const content = task.panel.querySelector('.task-workspace-panel__content');
    const fields = savedFields ?? serializePanel(task.panel);
    task.cleanup?.();
    task.cleanup = null;
    content.setAttribute('aria-busy', 'true');
    content.innerHTML = `<p class="workflow-loading">${copy.loading}</p>`;
    try {
      task.cleanup = await renderRoute(task.route, content) || null;
      content.removeAttribute('aria-busy');
      const identity = content.querySelector('[data-workspace-container-number]');
      const title = identity?.textContent?.trim() || compactTitle(content, task.route, copy.task);
      task.panel.querySelector('.task-workspace-panel__context').textContent = title;
      task.panel.setAttribute('aria-label', title);
      identity?.remove();
      restorePanel(content, fields);
      persist();
      return true;
    } catch (error) {
      content.removeAttribute('aria-busy');
      const errorState = document.createElement('div');
      errorState.className = 'workflow-empty is-error';
      const title = document.createElement('h2');
      title.textContent = copy.unavailable;
      const detail = document.createElement('p');
      detail.textContent = String(error?.message || copy.openFailed);
      errorState.append(title, detail);
      content.replaceChildren(errorState);
      return false;
    }
  }

  async function open(route, { activateTask = true, savedFields = [], savedState = {} } = {}) {
    const id = routeId(route);
    if (!id || destroyed) return false;
    if (tasks.has(id)) {
      if (activateTask) show(tasks.get(id));
      return true;
    }

    const panel = document.createElement('section');
    panel.className = 'task-workspace-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute('aria-label', copy.window);
    panel.innerHTML = `
      <header class="task-workspace-panel__chrome">
        <strong class="task-workspace-panel__context">${copy.task}</strong>
            <!-- One X (Alan, 2026-08-10: "上方的这些按钮变成一个 x 关闭按钮，删除别的
                 功能"). Four coloured dots read as window chrome and invited a guess
                 at which one files the Task; Apply does that, at the bottom right.
                 Up here there is one thing left to do and it closes the panel. -->
            <div class="task-workspace-panel__controls">
              <button class="task-workspace-close" type="button" aria-label="${copy.discard}">${CLOSE_ICON}</button>
            </div>
      </header>
      <div class="task-workspace-panel__content" aria-busy="true"><p class="workflow-loading">${copy.loading}</p></div>
      <!-- Submitting is not a traffic light (Alan, 2026-08-10: "把上面的这个点变成一个
           真正的蓝色 apply button 在右下方"). A green dot in a row of three coloured dots
           reads as window chrome - close, minimise, maximise - not as the thing that
           files the Task. It is a labelled blue button at the bottom right, where a
           form's confirm belongs and where the eye finishes reading. -->
      <footer class="task-workspace-panel__footer">
        <button class="task-workspace-done" type="button">${copy.done}</button>
      </footer>`;
    panels.append(panel);

    const layoutIndex = String(route).startsWith('#/inspect/')
      ? [...tasks.values()].filter((item) => String(item.route).startsWith('#/inspect/')).length
      : [...tasks.values()].filter((item) => !String(item.route).startsWith('#/inspect/')).length;
    const position = defaultPosition(layoutIndex, savedState, route);
    const workspaceKind = String(route).startsWith('#/inspect/') ? 'inspection' : 'action';
    panel.dataset.workspaceKind = workspaceKind;
    panel.style.setProperty('--task-panel-width', `${position.panelWidth}px`);
    panel.style.setProperty('--task-panel-max-height', `${position.panelMaxHeight}px`);
    const task = {
      id, route, panel, cleanup: null, dragCleanup: null,
      left: position.left, top: position.top,
      locked: savedState.locked === true,
      visible: savedState.visible !== false,
      ephemeral: String(route).startsWith('#/inspect/'),
    };
    tasks.set(id, task);
    task.dragCleanup = mountDragging(task);
    panel.querySelector('.task-workspace-close').addEventListener('click', () => close(id));
    panel.querySelector('.task-workspace-done').addEventListener('click', async (event) => {
      const button = event.currentTarget;
      if (button.disabled) return;
      button.disabled = true;
      try {
        const target = task.panel.querySelector('[data-workspace-done-target]:not([disabled])');
        const confirmed = await onDone({ id, route: task.route, panel: task.panel, target });
        if (confirmed === true && tasks.has(id)) close(id);
      } finally {
        if (tasks.has(id)) button.disabled = false;
      }
    });
    panel.addEventListener('pointerdown', () => bringToFront(task));
    panel.addEventListener('click', (event) => {
      if (event.target.closest('[data-workflow-back]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        hide(task);
      }
    }, true);

    syncTask(task);
    if (activateTask) show(task);
    else syncRestore();
    await renderTask(task, { savedFields });
    return true;
  }

  function intercept(event) {
    const anchor = event.target.closest?.('a[href]');
    const route = anchor?.getAttribute('href');
    if (!isWorkspaceTaskRoute(route)) return;
    event.preventDefault();
    void open(route);
  }

  function hideOnBlankDashboard(event) {
    if (!event.target.closest?.('.page')) return;
    if (event.target.closest('button, a, input, select, textarea, label, [role="button"], [contenteditable="true"], tr, td, th')) return;
    hideUnlocked();
  }

  function keyDown(event) {
    if (event.key !== 'Escape') return;
    const visibleUnlocked = [...tasks.values()]
      .filter((task) => task.visible && !task.locked)
      .sort((left, right) => Number(right.panel.style.zIndex || 0) - Number(left.panel.style.zIndex || 0));
    if (!visibleUnlocked.length) return;
    event.preventDefault();
    hide(visibleUnlocked[0]);
  }

  root.addEventListener('click', intercept, true);
  root.addEventListener('click', hideOnBlankDashboard);
  workspace.addEventListener('click', intercept, true);
  workspace.addEventListener('input', persist);
  workspace.addEventListener('change', persist);
  restore.addEventListener('click', restoreHidden);
  document.addEventListener('keydown', keyDown);

  void (async () => {
    for (const saved of safeReadState().tasks) {
      if (isWorkspaceTaskRoute(saved?.route)) {
        await open(saved.route, {
          activateTask: false,
          savedFields: saved.fields || [],
          savedState: saved,
        });
      }
    }
    syncRestore();
  })();

  return Object.freeze({
    open,
    async refreshRoute(route) {
      const id = routeId(route);
      const task = id ? tasks.get(id) : null;
      if (!task || destroyed) return false;
      return renderTask(task);
    },
    hideUnlocked,
    restoreHidden,
    confirmRoute,
    closeRoute(route) {
      const id = routeId(route);
      if (id) close(id);
    },
    setDockHost,
    closeAll() { [...tasks.keys()].forEach(close); },
    destroy() {
      persist();
      destroyed = true;
      for (const task of tasks.values()) {
        task.cleanup?.();
        task.dragCleanup?.();
        task.panel.remove();
      }
      tasks.clear();
      root.removeEventListener('click', intercept, true);
      root.removeEventListener('click', hideOnBlankDashboard);
      workspace.removeEventListener('click', intercept, true);
      document.removeEventListener('keydown', keyDown);
      restore.remove();
      currentDockHost?.classList.remove('has-hidden-task-windows');
      workspace.remove();
    },
  });
}
